import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import CookieConsent from './components/CookieConsent';
import NotificationPrompt from './components/NotificationPrompt';

// ScrollToTop component - scrolls to top on every route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// MetaPixelPageView — fires fbq('track', 'PageView') on every SPA route change.
// The base pixel in index.html only fires on the first load; this covers client-side navigation.
const MetaPixelPageView = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      window.fbq('track', 'PageView');
    }
  }, [pathname]);
  return null;
};

// Pages
import LandingPage from './pages/LandingPage';
import SubscribePage from './pages/SubscribePage';
import RegisterPage from './pages/RegisterPage';
import LoginPage from './pages/LoginPage';
import CheckoutPage from './pages/CheckoutPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';
import Dashboard from './pages/Dashboard';
import PublicProfilePage from './pages/PublicProfilePage';
import SearchPage from './pages/SearchPage';
import SettingsPage from './pages/SettingsPage';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminDashboard from './pages/AdminDashboard';
import BlogPage from './pages/BlogPage';
import AboutPage from './pages/AboutPage';
import FAQPage from './pages/FAQPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import ContactPage from './pages/ContactPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import BrandPortfolioPage from './pages/BrandPortfolioPage';
import SubscriptionManagementPage from './pages/SubscriptionManagementPage';
import SubscriptionBanner from './components/SubscriptionBanner';
import SuspendedPaywall from './components/SuspendedPaywall';
import ResubscribeWall from './components/ResubscribeWall';
import UpdatePaymentMethodPage from './pages/UpdatePaymentMethodPage';
import FreeTrialPage from './pages/FreeTrialPage';
import CollaborationsPage from './pages/CollaborationsPage';
import InfluencerPortfolioPage from './pages/InfluencerPortfolioPage';
import NotificationsPage from './pages/NotificationsPage';
import BiometricPolicyPage from './pages/BiometricPolicyPage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import AcceptableUsePage from './pages/AcceptableUsePage';

// PWA Update Checker - checks for service worker updates periodically
const PWAUpdateChecker = () => {
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      const checkForUpdate = () => {
        navigator.serviceWorker.getRegistration().then((reg) => {
          if (reg) reg.update();
        });
      };
      // Check every 30 min + on visibility return
      const interval = setInterval(checkForUpdate, 30 * 60 * 1000);
      const handleVisibility = () => {
        if (!document.hidden) checkForUpdate();
      };
      document.addEventListener('visibilitychange', handleVisibility);
      return () => {
        clearInterval(interval);
        document.removeEventListener('visibilitychange', handleVisibility);
      };
    }
  }, []);
  return null;
};

// Wrapper that conditionally includes NotificationProvider (skip for admin routes)
const ConditionalNotificationProvider = ({ children }) => {
  const location = useLocation();
  if (location.pathname.startsWith('/admin')) {
    return <>{children}</>;
  }
  return <NotificationProvider>{children}</NotificationProvider>;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <ConditionalNotificationProvider>
        <ScrollToTop />
        <MetaPixelPageView />
        <div className="App">
          {/* Global subscription-status banner (past_due + suspended, non-dismissible) */}
          <SubscriptionBanner />
          {/* Hard paywall for fully suspended accounts (post 7-day grace) — full-screen, non-dismissible */}
          <SuspendedPaywall />
          {/* Resubscribe prompt for canceled accounts — invitation, not warning */}
          <ResubscribeWall />
          <Routes>
            {/* Public Pages */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/try" element={<FreeTrialPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/biometric-policy" element={<BiometricPolicyPage />} />
            <Route path="/refund-policy" element={<RefundPolicyPage />} />
            <Route path="/acceptable-use" element={<AcceptableUsePage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/search" element={<SearchPage />} />
            
            {/* Auth Pages */}
            <Route path="/subscribe" element={<SubscribePage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
            
            {/* User Pages */}
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/portfolio" element={<BrandPortfolioPage />} />
            <Route path="/collaborations" element={<CollaborationsPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/subscription" element={<SubscriptionManagementPage />} />
            <Route path="/subscription/update-payment" element={<UpdatePaymentMethodPage />} />
            <Route path="/influencer/:handle" element={<InfluencerPortfolioPage />} />
            <Route path="/profile/:handle" element={<PublicProfilePage />} />
            
            {/* Admin Pages */}
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/admin" element={<AdminDashboard />} />
          </Routes>
          <NotificationPrompt />
          <CookieConsent />
          <PWAUpdateChecker />
        </div>
        </ConditionalNotificationProvider>
      </Router>
    </AuthProvider>
  );
}

export default App;
